// The remembered switches (CALENDAR-C1 §3): the Calendar's "Tasks" overlay and the board
// calendar's "Events" overlay, per person (the key carries the membership id, so two accounts on
// one browser keep their own). Kept on this device (localStorage, try/catch) until `me.prefs`
// gains a key for them; one tiny store per key so the sidebar's switch and the screen agree.
import { useQuery } from "@tanstack/react-query";
import { useCallback, useSyncExternalStore } from "react";
import type { Api } from "../../api/client";
import { meQuery } from "../../api/query";
import { BOARD_EVENTS_KEY, readToggle, TASKS_TOGGLE_KEY, writeToggle } from "./calendar-model";

const listeners = new Map<string, Set<() => void>>();
const memory = new Map<string, boolean>();

function read(key: string): boolean {
  if (!memory.has(key)) memory.set(key, readToggle(key));
  return memory.get(key) ?? false;
}

function useToggle(key: string): [boolean, (on: boolean) => void] {
  const subscribe = useCallback(
    (cb: () => void) => {
      let set = listeners.get(key);
      if (!set) {
        set = new Set();
        listeners.set(key, set);
      }
      set.add(cb);
      return () => set.delete(cb);
    },
    [key],
  );
  const value = useSyncExternalStore(
    subscribe,
    () => read(key),
    () => false,
  );
  const set = useCallback(
    (on: boolean) => {
      memory.set(key, on);
      writeToggle(key, on);
      for (const cb of listeners.get(key) ?? []) cb();
    },
    [key],
  );
  return [value, set];
}

/** A switch's key for one person (the company when the caller has no membership in it). */
export const toggleKey = (base: string, membershipId: string | null, co: string) =>
  `${base}.${membershipId ?? co}`;

function usePersonKey(base: string, api: Api, co: string): string {
  const me = useQuery({ ...meQuery(api), retry: false });
  const membership = me.data?.memberships.find((m) => m.company_id === co)?.membership_id;
  return toggleKey(base, membership ?? null, co);
}

/** The Calendar's "Tasks" overlay, for the caller. */
export const useTasksOverlay = (api: Api, co: string) =>
  useToggle(usePersonKey(TASKS_TOGGLE_KEY, api, co));

/** The board calendar's "Events" overlay, for the caller. */
export const useBoardEventsOverlay = (api: Api, co: string) =>
  useToggle(usePersonKey(BOARD_EVENTS_KEY, api, co));

/** Tests: forget the in-memory values (localStorage is cleared by the test itself). */
export function resetCalendarToggles(): void {
  memory.clear();
}
